import React, { useState } from 'react';
import styles from './TournamentChallengesTable.module.scss';
import { ITournamentChallenge } from '../../../../types/ITournament';
import { format } from 'date-fns';
import { DATE_FORMAT } from '../../../../constants';
import { GreenButton } from '../../../Shared/GreenButton/GreenButton';
import { PopupConfirmDialog } from '../../../Shared/PopupConfirmDialog/PopupConfirmDialog';
import { useApplyAction } from '../../../../hooks/useApplyAction';
import { ChallengeStatusBadge } from '../../../Shared/ChallengeStatusBadge/ChallengeStatusBadge';
import { TwitchLink } from '../../../Shared/TwitchLink/TwitchLink';

const tableHeader: string[] = ['CHALLENGE ID', 'STATUS', 'STAGE', 'ROUND', 'SCHEDULED', 'CAPACITY', 'AUTO WIN', 'ABANDONED', 'TEAMS', ''];

interface IProps {
    challenges: ITournamentChallenge[];
    onChallengeClick?: (challengeId: string) => void;
    actions?: string[];
    refetch?: () => void;
}

export const TournamentChallengesTable = ({ challenges, onChallengeClick, actions, refetch }: IProps): React.JSX.Element => {
    const { mutate: applyAction } = useApplyAction();
    const [changeStatusId, setChangeStatusId] = useState('');

    const onChangeStatus = (status: string) => {
        if (status) {
            applyAction(
                {
                    challengeId: changeStatusId,
                    action: status,
                },
                {
                    onSuccess() {
                        onCloseModal();
                        refetch?.();
                    },
                    onError(e) {
                        console.log('e', e.response);
                        alert(JSON.stringify(e?.response?.data ?? '{}') ?? e);
                    },
                }
            );
        }
    };

    const onCloseModal = () => {
        setChangeStatusId('');
    };
    if (!challenges || challenges.length === 0) {
        return <div className={styles.empty}>No challenges found</div>;
    }

    return (
        <div className={styles.container}>
            <PopupConfirmDialog title="Select action" description="" show={!!changeStatusId} onClose={onCloseModal}>
                {actions?.map((action) => (
                    <div key={action} className={styles.action}>
                        <GreenButton
                            mini
                            outline
                            onClick={() => {
                                onChangeStatus(action);
                                console.log('action', action);
                            }}
                        >
                            {action}
                        </GreenButton>
                    </div>
                ))}
            </PopupConfirmDialog>

            <table className={styles.table}>
                <thead>
                    <tr>
                        {tableHeader.map((h) => (
                            <td key={h}>{h}</td>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {challenges.map((challenge, idx) => {
                        // Twitch is taken only from the first captain across all participants.
                        const host = challenge.teams.flatMap((team) => team.participants).find((p) => p.isCaptain);

                        return (
                        <tr
                            key={challenge.challengeId}
                            className={`${idx % 2 ? '' : styles.grayColl} ${onChallengeClick ? styles.clickable : ''}`}
                            onClick={() => onChallengeClick?.(challenge.challengeId)}
                        >
                            <td className={styles.idCell}>{challenge.challengeId}</td>
                            <td>
                                <ChallengeStatusBadge status={challenge.status} />
                            </td>
                            <td>{challenge.stage}</td>
                            <td>{challenge.roundName}</td>
                            <td>{format(new Date(challenge.scheduledAt), DATE_FORMAT)}</td>
                            <td>{challenge.capacity}</td>
                            <td>{challenge.isAutoWin ? 'Yes' : 'No'}</td>
                            <td>{challenge.isAbandoned ? 'Yes' : 'No'}</td>
                            <td>
                                {challenge.teams.map((team) => (
                                    <div key={team.name} className={styles.team}>
                                        <span className={styles.teamName}>{team.name}</span>
                                        {team.participants.map((p) => (
                                            <span key={p.userId} className={styles.participant}>
                                                {p.gameNickName || p.userId}
                                                {host && p.userId === host.userId && (
                                                    <TwitchLink handle={host.linkedAccounts?.Twitch} />
                                                )}
                                                {p.result?.gameResult ? ` (${p.result.gameResult})` : ''}
                                            </span>
                                        ))}
                                    </div>
                                ))}
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                                {actions && actions.length > 0 && (
                                    <div className={styles.row}>
                                        <GreenButton
                                            noMargin
                                            mini
                                            onClick={() => {
                                                setChangeStatusId(challenge.challengeId);
                                            }}
                                        >
                                            Change status
                                        </GreenButton>
                                    </div>
                                )}
                            </td>
                        </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
